import React, { useContext, useEffect, useMemo } from "react";
import { ArrowBack } from "@mui/icons-material";
import PageContainer from "src/components/Layout/PageContainer";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import useDeviceSelector from "src/components/Widget/DeviceSelector";
import { ClientContext } from "src/contexts/GqlContext";
import { getProgramsByDevices } from "src/services/graphql/Program";
import { useQuery } from "@apollo/client";
import {
  RegularProgram as Program,
  ProgramType,
} from "src/interfaces/IProgram";
import _get from "lodash/get";
import { useTheme } from "@mui/material";
import { Page, Context as PageContext } from "../../PageController";
import ProgramCard from "./ProgramCard";
import { makeEditProgramPage } from "../EditProgramPage";
import useScheduleTab, { ScheduleTabType } from "./useScheduleTab";
import AlenScheduling from "./AlenScheduling";

type Props = { propertyName: string };

export type ViewProgramPage = Page<Props>;

export const pageName = "view-schedules";

export const makeViewProgramPage = (props: Props): ViewProgramPage => ({
  name: pageName,
  props,
});

export function Component({ propertyName }: Props) {
  const { client } = useContext(ClientContext);
  const { property, devices, formElement, floors, alenDevices } =
    useDeviceSelector(client, {
      controlledValue: { propertyName, floors: [] },
      hideProperty: true,
    });
  const { backToLastPage, pushToPage, page } = useContext(PageContext);

  const routeToProgram = (program: Program) =>
    pushToPage(makeEditProgramPage({ program }));

  const { data, refetch } = useQuery(
    getProgramsByDevices(client.client_dbname),
    {
      variables: {
        device_ids: devices.map((d) => d.device_id),
        program_type: ProgramType.REGULAR,
      },
    }
  );

  useEffect(() => {
    // Workaround to refetch when page is re-entered. So Delete, Edit program will be rendered correctly.
    if (page.name === pageName) {
      refetch();
    }
  }, [page.name, refetch]);

  const programs: Program[] = _get(data, `${client.client_dbname}_program`, []);

  const theme = useTheme();

  const tabOptions = useMemo(() => {
    const options: ScheduleTabType[] = [];
    if (property?.property_id !== -1) {
      options.push(ScheduleTabType.WYND);
    }
    if (property?.alen_id) {
      options.push(ScheduleTabType.ALEN);
    }
    return options;
  }, [property]);

  const { currentTab, tabElement } = useScheduleTab({ options: tabOptions });

  return (
    <PageContainer>
      <Stack direction="row" sx={{ marginBottom: 5 }}>
        <IconButton onClick={() => backToLastPage()}>
          <ArrowBack sx={{ width: "32px", height: "32px" }} />
        </IconButton>
        <Typography variant="h3" sx={{ margin: "0 1rem" }}>
          View Schedule
        </Typography>
      </Stack>
      {tabElement}
      {currentTab === ScheduleTabType.WYND && (
        <>
          <Stack>{formElement}</Stack>
          <Stack
            sx={{
              paddingY: "1rem",
              display: "grid",
              gap: 3,
              [theme.breakpoints.down("sm")]: {
                gridTemplateColumns: "1fr",
              },
              [theme.breakpoints.up("md")]: {
                gridTemplateColumns: "1fr 1fr",
              },
            }}
          >
            {programs.map((program) => (
              <ProgramCard
                key={program.program_id}
                program={program}
                routeToProgram={routeToProgram}
              />
            ))}
            {programs.length === 0 && (
              <Typography>No program found for selected Property</Typography>
            )}
          </Stack>
        </>
      )}
      {property?.alen_id && currentTab === ScheduleTabType.ALEN && (
        <AlenScheduling
          alenPropertyId={property.alen_id}
          propertyName={property.property_name}
          floors={floors}
          alenDevices={alenDevices}
        />
      )}
    </PageContainer>
  );
}
